import { ReactElement, useCallback, useEffect, useState } from 'react'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useParams } from 'react-router'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { EvidenceOfEffectivenessFileUploadModal } from 'src/screens/shared/evidence-of-effectiveness/EvidenceOfEffectivenessFileUploadModal'
import Card from '@mui/material/Card'
import { Box, Button, CardContent } from '@mui/material'
import { UploadIcon } from 'src/shared/icons/Icons'
import {
  Factsheet_Effectiveness_Evaluation,
  Factsheet_Effectiveness_Evaluation_Insert_Input,
  File as GraphqlFile,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import {
  deleteFactsheetEffectivenessEvaluationMutation,
  fetchFactsheetEvidenceOfEffectivenessEdit,
  insertFactsheetEffectivenessEvaluationMutation,
} from 'src/screens/factsheets/factsheet/factsheetQueries'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import {
  EvaluationFileItem,
  EvidenceOfEffectivenessFilesGrid,
} from 'src/screens/shared/evidence-of-effectiveness/EvidenceOfEffectivenessFilesGrid'
import { FileService } from 'src/service/axios/FileService'
import { ENTITY_TYPE, FILE_TYPE } from 'src/shared/constants/constants'
import { queryFiles } from 'src/shared/presentation/fileQueries'
import { mapToEvaluationFileItem } from 'src/screens/shared/evidence-of-effectiveness/utils/EvidenceOfEffectivenessUtils'
import { usePermissionsForFactsheetDetailsDescriptionPage } from 'src/service/security/PermissionHook'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { usePrompt } from 'src/routing/routing-hooks'

export const FactsheetEvidenceOfEffectivenessEditPage = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const { factsheetId } = useParams()
  const factsheet_id = parseInt(factsheetId as string)

  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [loading, setLoading] = useState(false)
  const [isDirtyUploadModal, setIsDirtyUploadModal] = useState(false)
  const [isUploadModalSubmitted, setIsUploadModalSubmitted] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  const [factsheetEvaluationFileItems, setFactsheetEvaluationFileItems] = useState<EvaluationFileItem[]>([])
  const [factsheetEvaluationFileItemsToBeDeleted, setFactsheetEvaluationFileItemsToBeDeleted] = useState<
    EvaluationFileItem[]
  >([])

  const {
    canEdit,
    loading: loadingPermissions,
    metadata: { enableEvaluationFileUpload },
  } = usePermissionsForFactsheetDetailsDescriptionPage(factsheet_id)

  // data fetch
  const initData = useCallback(async () => {
    setLoading(true)

    try {
      // fetch evaluations
      const { data } = await urqlClient
        .query<{
          factsheet_effectiveness_evaluation: Query_Root['factsheet_effectiveness_evaluation']
        }>(fetchFactsheetEvidenceOfEffectivenessEdit, { id: factsheet_id })
        .toPromise()

      if (data?.factsheet_effectiveness_evaluation === undefined || data?.factsheet_effectiveness_evaluation === null) {
        notificationService.operationFailed()
        return
      }

      // fetch files for evaluations
      const { data: dataFiles } = await urqlClient
        .query<
          {
            file: Query_Root['file']
          },
          {
            fileType: string
            entityType: string
            entityIds: number[]
          }
        >(queryFiles, {
          fileType: FILE_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
          entityType: ENTITY_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
          entityIds: data.factsheet_effectiveness_evaluation.map((e) => e.id) ?? [],
        })
        .toPromise()

      if (dataFiles?.file === undefined || dataFiles?.file === null) {
        notificationService.operationFailed()
        return
      }

      const factsheetEvaluation: Array<Factsheet_Effectiveness_Evaluation> = data.factsheet_effectiveness_evaluation
      const factsheetEvaluationFiles: Array<GraphqlFile> = dataFiles.file

      const evaluationFileItems: EvaluationFileItem[] = factsheetEvaluation.map((evaluation) => {
        const file = factsheetEvaluationFiles.find((f) => f.entity_id === evaluation.id)
        return mapToEvaluationFileItem(evaluation, file)
      })

      setFactsheetEvaluationFileItems(evaluationFileItems)
    } catch (error) {
      notificationService.operationFailed()
    } finally {
      setLoading(false)
    }
  }, [factsheet_id, notificationService, urqlClient])

  useEffect(() => {
    // Load data only if file upload is enabled (new factsheets)
    if (enableEvaluationFileUpload === true) {
      initData()
    }
  }, [enableEvaluationFileUpload, initData])

  // handlers
  const handleFileUploadsAndDeletions = useCallback(
    async (
      fileItems: EvaluationFileItem[],
      deletedFileItems: EvaluationFileItem[],
      factsheetId: string,
    ): Promise<boolean> => {
      let hasErrorInsertingFiles = false

      try {
        // Insert or update files and evaluations
        for (const fileItem of fileItems) {
          // Prepare single evaluation object
          const evaluationToInsert: Factsheet_Effectiveness_Evaluation_Insert_Input = {
            ...(fileItem.entityId != null ? { id: fileItem.entityId } : {}), // if entityId exists, include it for update otherwise it's an insert
            factsheet_id: parseInt(factsheetId),
            evaluation_index: fileItem.evaluationIndex,
            name: fileItem.name,
            author: fileItem.author,
            year_publication: fileItem.yearPublication,
            publication_authorized: fileItem.publish,
          }

          // Insert single evaluation
          const { data, error } = await urqlClient
            .mutation<{
              insert_factsheet_effectiveness_evaluation_one: Mutation_Root['insert_factsheet_effectiveness_evaluation_one']
            }>(insertFactsheetEffectivenessEvaluationMutation, {
              object: evaluationToInsert,
            })
            .toPromise()

          if (error || !data?.insert_factsheet_effectiveness_evaluation_one) {
            console.log(error)
            hasErrorInsertingFiles = true
            continue
          }

          // Get the inserted/updated entity ID
          const entityId = data.insert_factsheet_effectiveness_evaluation_one.id

          // Upload file if it doesn't have a UUID (new file) and is valid file
          if ((fileItem.file as any)?.isValid === false) {
            continue
          }

          if (fileItem.fileUUID === null && fileItem.file) {
            try {
              await FileService.upload(
                fileItem.file,
                FILE_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
                ENTITY_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
                entityId,
                fileItem.file.name,
              )
            } catch (error) {
              hasErrorInsertingFiles = true
            }
          }
        }

        // Delete files and evaluations that were removed
        for (const fileItem of deletedFileItems) {
          // Delete file if it has a UUID
          if (fileItem.fileUUID !== null) {
            try {
              await FileService.remove(fileItem.fileUUID)
            } catch (error) {
              hasErrorInsertingFiles = true
            }
          }

          // Delete evaluation
          if (fileItem.entityId !== null) {
            const { data, error } = await urqlClient
              .mutation<{
                delete_factsheet_effectiveness_evaluation_by_pk: Mutation_Root['delete_factsheet_effectiveness_evaluation_by_pk']
              }>(deleteFactsheetEffectivenessEvaluationMutation, {
                id: fileItem.entityId,
              })
              .toPromise()

            if (!data?.delete_factsheet_effectiveness_evaluation_by_pk) {
              console.log('No evaluation deleted')
              continue
            }

            if (error) {
              console.log(error)
              hasErrorInsertingFiles = true
              continue
            }
          }
        }
      } catch (error) {
        console.log(error)
        throw error
      }

      return hasErrorInsertingFiles
    },
    [urqlClient],
  )

  // form save
  const submit = useCallback(async () => {
    setLoading(true)

    try {
      let hasErrorInsertingFiles = false

      // Only proceed with uploads and deletions if the modal was submitted
      if (isUploadModalSubmitted) {
        hasErrorInsertingFiles = await handleFileUploadsAndDeletions(
          factsheetEvaluationFileItems,
          factsheetEvaluationFileItemsToBeDeleted,
          factsheetId as string,
        )
      }

      if (hasErrorInsertingFiles) {
        notificationService.info(getMessage('notification.factsheet.edit.evidence_of_effectiveness.partial_failure'))
      } else {
        notificationService.changesSaved()
      }
    } catch (error) {
      notificationService.operationFailed()
    } finally {
      setLoading(false)
      setIsDirtyUploadModal(false)
      setIsUploadModalSubmitted(false)
    }
  }, [
    isUploadModalSubmitted,
    handleFileUploadsAndDeletions,
    factsheetEvaluationFileItems,
    factsheetEvaluationFileItemsToBeDeleted,
    factsheetId,
    notificationService,
    getMessage,
  ])

  const onSave = useCallback(
    async (_event: any) => {
      await submit()
        .then(() => {
          initData()
        })
        .catch(() => {
          notificationService.operationFailed()
        })
    },
    [initData, notificationService, submit],
  )

  const onBack = useCallback(() => {
    const route = ROUTES.FactsheetDescriptionRoot.params({ factsheetId })
    navigate(`${route}#evidence-of-effectiveness`)
  }, [factsheetId, navigate])

  const onSaveAndBack = useCallback(
    async (_event: any) => {
      try {
        await submit()
        onBack()
      } catch {
        // do nothing
      }
    },
    [onBack, submit],
  )

  // Upload Modal handlers
  const handleOpenEvidenceOfEffectivenessFileUploadModal = useCallback(() => {
    setUploadModalOpen(true)
  }, [])

  const handleCloseEvidenceOfEffectivenessFileUploadModal = useCallback(() => {
    setUploadModalOpen(false)
  }, [])

  const handleSaveEvidenceOfEffectivenessFileUploadModal = useCallback(
    (values: EvaluationFileItem[], deletedItems: EvaluationFileItem[]) => {
      // modal was submitted with modified values inside
      if (isDirtyUploadModal) {
        setIsUploadModalSubmitted(true)
      }
      setFactsheetEvaluationFileItems(values)
      setFactsheetEvaluationFileItemsToBeDeleted(deletedItems)
    },
    [isDirtyUploadModal],
  )

  usePrompt(getMessage('notification.form.unsaved.changes'), isUploadModalSubmitted || isDirtyUploadModal)

  // Authorization check
  if (!loadingPermissions && !canEdit) {
    return <NotAuthorized inEditPage />
  }

  // Special case: cannot access new edit page for Evaluation Effectiveness field if file upload is disabled
  if (!loadingPermissions && (enableEvaluationFileUpload === false || enableEvaluationFileUpload === null)) {
    return <NotAuthorized inEditPage />
  }

  return (
    <>
      <ScreenLayout
        title={getMessage('label.factsheet.edit.evidence_of_effectiveness')}
        onBack={onBack}
        hasSecondLevelNavigation={false}
        actions={
          <>
            <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
            <SaveButton origin="header" onClick={onSave} />
          </>
        }
      >
        <PageLayout>
          {!loadingPermissions && (
            <>
              {/* Uploaded File Section */}
              <Section
                id={'evidence-of-effectiveness-file-upload'}
                title={getMessage('label.factsheet.edit.evidence_of_effectiveness.file_upload.section.title')}
                actionButton={
                  <Button
                    variant="text"
                    startIcon={<UploadIcon />}
                    onClick={handleOpenEvidenceOfEffectivenessFileUploadModal}
                  >
                    {getMessage('button.manage-uploads')}
                  </Button>
                }
                helpAndInstructions={
                  <HelpAndInstructions labelKey="label.help.factsheet.edit.evidence_of_effectiveness.file_upload.section.title" />
                }
              >
                <Card>
                  <CardContent>
                    <Box>
                      <EvidenceOfEffectivenessFilesGrid
                        editMode={false}
                        showPublishCheckbox={true}
                        loading={loading}
                        rows={factsheetEvaluationFileItems}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Section>
            </>
          )}
        </PageLayout>
      </ScreenLayout>

      <EvidenceOfEffectivenessFileUploadModal
        helpAndInstructionsKey={'label.help.factsheet.modal-dialog.evidence_of_effectiveness.file_upload.section.title'}
        initialValues={factsheetEvaluationFileItems}
        initialDeletedItems={factsheetEvaluationFileItemsToBeDeleted}
        open={uploadModalOpen}
        setDirtyUploadModal={setIsDirtyUploadModal}
        onCancel={handleCloseEvidenceOfEffectivenessFileUploadModal}
        onSave={handleSaveEvidenceOfEffectivenessFileUploadModal}
      />
    </>
  )
}
